import { useCallback, useEffect, useRef, useState } from 'react';

import type { PublicRunSnapshot } from '../../../contracts';
import type { RunStreamEventObserver } from '../../query/useRunStreamLifecycle';
import {
  discoverChildRunFromStreamEvent,
  reconcileChildRunDiscoveriesFromSnapshot,
  withUpsertedChildRunDiscovery,
  type ChildRunDiscovery,
  type ChildRunKind,
} from './discovery';

export interface ChildRunDiscoveries {
  discoveries: ChildRunDiscovery[];
  onParentStreamEvent: RunStreamEventObserver;
  removeDiscovery: (childRunId: string) => void;
  reconcileFromSnapshot: (run: PublicRunSnapshot | null | undefined) => void;
}

export const useChildRunDiscoveries = (
  kind: ChildRunKind,
  agentRunId: string | undefined
): ChildRunDiscoveries => {
  const [discoveries, setDiscoveries] = useState<ChildRunDiscovery[]>([]);
  const endedChildRunIdsRef = useRef<Set<string>>(new Set());
  const sawInitialSnapshotRef = useRef(false);

  useEffect(() => {
    endedChildRunIdsRef.current = new Set();
    sawInitialSnapshotRef.current = false;
    setDiscoveries((current) => (current.length === 0 ? current : []));
  }, [agentRunId]);

  const onParentStreamEvent = useCallback<RunStreamEventObserver>(
    (_id, event) => {
      if (!agentRunId) return;
      const discovery = discoverChildRunFromStreamEvent(kind, agentRunId, event);
      if (!discovery || endedChildRunIdsRef.current.has(discovery.childRunId)) return;
      setDiscoveries((current) => withUpsertedChildRunDiscovery(current, discovery));
    },
    [agentRunId, kind]
  );

  const removeDiscovery = useCallback((childRunId: string) => {
    endedChildRunIdsRef.current.add(childRunId);
    setDiscoveries((current) => current.filter((item) => item.childRunId !== childRunId));
  }, []);

  const reconcileFromSnapshot = useCallback(
    (run: PublicRunSnapshot | null | undefined) => {
      if (!run) return;
      const restored = !sawInitialSnapshotRef.current;
      sawInitialSnapshotRef.current = true;
      for (const child of run.child_runs ?? []) {
        if (child.kind === kind && child.status !== 'running') {
          endedChildRunIdsRef.current.add(child.agent_run_id);
        }
      }
      setDiscoveries((current) => {
        const next = reconcileChildRunDiscoveriesFromSnapshot(
          kind,
          current,
          run,
          restored
        );
        const filtered = next.filter(
          (discovery) => !endedChildRunIdsRef.current.has(discovery.childRunId)
        );
        return filtered.length === next.length ? next : filtered;
      });
    },
    [kind]
  );

  return { discoveries, onParentStreamEvent, removeDiscovery, reconcileFromSnapshot };
};
